import apiClient from '../../api/client';
import { getExecutorById } from '../../api/executorsApi.ts';

const CACHE_TTL = 2 * 60 * 1000;
const cache = new Map();
const scopedKey = (key) => `${localStorage.getItem('accessToken') || 'guest'}:${key}`;

const getCached = (key, loader, ttl = CACHE_TTL) => {
  const storageKey = scopedKey(key);
  const now = Date.now();
  const cached = cache.get(storageKey);

  if (cached?.data !== undefined && cached.expiresAt > now) {
    return Promise.resolve(cached.data);
  }

  if (cached?.promise) return cached.promise;

  const promise = loader()
    .then((data) => {
      cache.set(storageKey, { data, expiresAt: Date.now() + ttl });
      return data;
    })
    .catch((error) => {
      cache.delete(storageKey);
      throw error;
    });

  cache.set(storageKey, { promise });
  return promise;
};

export const invalidatePersonalAccountCache = (...keys) => {
  keys.forEach(key => cache.delete(scopedKey(key)));
};

export const updatePersonalAccountCache = (key, updater) => {
  const storageKey = scopedKey(key);
  const cached = cache.get(storageKey);
  if (cached?.data === undefined) return;
  const data = typeof updater === 'function' ? updater(cached.data) : updater;
  cache.set(storageKey, { data, expiresAt: Date.now() + CACHE_TTL });
};

export const getCachedCustomerProfile = () =>
  getCached('customer-profile', () =>
    apiClient.get('/customers/me/profile').then(response => response.data)
  );

export const getCachedCustomerNotifications = () =>
  getCached('customer-notifications', () =>
    apiClient.get('/customers/me/notifications').then(response => response.data)
  );

export const getCachedCustomerReviews = () =>
  getCached('customer-reviews', () =>
    apiClient.get('/customers/me/reviews/received', {
      params: { page: 0, size: 20 },
    }).then(response => response.data?.page?.items || [])
  );

export const getCachedProfileBlockStatus = () =>
  getCached('customer-block-status', () =>
    apiClient.get('/auth/profile-block-status').then(response => response.data)
  );

export const getCachedCustomerOrders = () =>
  getCached('customer-orders', () =>
    apiClient.get('/customers/me/orders').then(response => response.data || [])
  );

export const getCachedFavoriteIds = () =>
  getCached('customer-favorite-ids', () =>
    apiClient.get('/customers/me/relations/executors/favorites')
      .then(response => response.data || [])
  );

export const getCachedExecutor = (executorId) =>
  getCached(`executor-${executorId}`, () => getExecutorById(executorId));

export const getCachedOrderInvitations = (orderId) =>
  getCached(`order-invitations-${orderId}`, () =>
    apiClient.get(`/customers/me/orders/${orderId}/invitations`)
      .then(response => response.data)
  );
